/**
 * @fileoverview Componente sidebar para creación de nuevos chats con clientes, gestión de plantillas y envío de mensajes WhatsApp
 * @module NewChat
 * @category Component - Organism
 * @since 1.0.0
 * @version 3.2.0
 */

import React, {
  useState, useMemo, useCallback, memo, useEffect
} from "react";
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import useNewChatMsIntegration from 'presentation/hooks/api-template-message/data/useNewChatMsIntegration';
import FindChat from "../modules/NewChat/FindChat";
import NewChatForm from "../modules/NewChat/NewChatForm";
import ApiTemplateMessage from "../modules/NewChat/ApiTemplateMessage";
import QrMessage from "../modules/NewChat/QrMessage";
import EmptyScreenInboxMC from "../components/EmptyScreenInboxMC";
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import FormProviderMC from '../components/FormProviderMC';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import useSelectedConversation from '../hooks/shared/useSelectedConversation';
import useWhatsappIntegrationStatus from '../modules/Integrations/useWhatsappIntegrationStatus';

/**
 * @typedef {Object} Customer
 * @description Objeto cliente con información completa de contacto
 * @property {string} id - Identificador único del cliente
 * @property {string} first_name - Nombre del cliente
 * @property {string} last_name - Apellido del cliente
 * @property {string} [phone] - Número telefónico del cliente
 * @property {string} [email] - Correo electrónico del cliente
 * @property {boolean} [blocked] - Estado de bloqueo del cliente
 * @property {Object} [metadata] - Información adicional del cliente
 */

/**
 * @typedef {Object} RetailerInfo
 * @description Configuración y estado del retailer
 * @property {boolean} whatsapp_integrated - Estado de integración WhatsApp
 * @property {boolean} qr_integrated - Estado de integración QR
 * @property {Object} whatsapp_config - Configuración específica de WhatsApp
 * @property {string} gupshup_app_id - ID de aplicación Gupshup
 * @property {string} gupshup_app_token - Token de aplicación Gupshup
 */

/**
 * @typedef {Object} Conversation
 * @description Objeto conversación activa
 * @property {string} id - Identificador de la conversación
 * @property {Customer} customer - Cliente asociado a la conversación
 * @property {Array} messages - Array de mensajes de la conversación
 * @property {string} status - Estado actual de la conversación
 */

/**
 * @typedef {Object} StepConfig
 * @description Configuración de botones y acciones para cada paso del flujo
 * @property {string} title - Título mostrado en el header del modal
 * @property {string|null} cancelLabel - Texto del botón cancelar
 * @property {string|null} submitLabel - Texto del botón enviar
 * @property {function|null} cancelAction - Función ejecutada al cancelar
 * @property {function|null} submitAction - Función ejecutada al enviar
 * @property {boolean} showSubmit - Mostrar o no el botón de envío
 */

/**
 * Componente Organism para gestión completa de creación de nuevos chats y envío de mensajes.
 * 
 * Implementa el patrón **Organism** de Atomic Design con flujo de steps optimizado
 * para crear conversaciones, seleccionar clientes y enviar mensajes WhatsApp.
 */
const NewChat = memo(({ hasCustomer = false, type = 'template' }) => {
  const { t } = useTranslation('NewChat');
  
  /**
   * Estado del step actual en el flujo de creación de chat.
   */
  const [step, setStep] = useState(hasCustomer ? 3 : 1);
  const [isOutboundSending, setIsOutboundSending] = useState(false);

  const dispatch = useDispatch();

  const setOutboundSending = useCallback((sending) => {
    setIsOutboundSending(!!sending);
  }, []);

  useEffect(() => {
    if (step !== 3) setIsOutboundSending(false);
  }, [step]);
  
  /**
   * Hook para obtener la conversación seleccionada actualmente.
   */
  const conversation = useSelectedConversation();
  
  /**
   * Métodos de react-hook-form para gestión de formularios.
   */
  const methods = useForm();
  
  /**
   * Selector optimizado para cliente seleccionado en el estado Redux.
   */
  const selected = useSelector((state) => state.customersReducer.selected);
  
  /**
   * Selector optimizado para información del retailer y configuraciones.
   */
  const retailerInfo = useSelector((state) => state.retailerUsersReducer.retailer_info);
  
  /**
   * Estado de conexión de WhatsApp resuelto igual que en el resto del inbox.
   *
   * `retailer_info.whatsapp_integrated` solo mira los flags legacy (Gupshup / QR), así que un
   * retailer conectado por BSP Mercately o servido por el microservicio da `false` y el modal
   * dejaba de montar `FindChat`. El hook mezcla el estado de `platform_retailers` del MS.
   */
  const { isWhatsappConnected } = useWhatsappIntegrationStatus(retailerInfo);

  const isMsWhatsapp = retailerInfo?.inbox_microservice_platforms?.whatsapp_messages === true;

  /**
   * Selector optimizado para cliente actual con fallback a conversación.
   */
  const currentCustomer = useSelector((state) => (state.customersReducer.customer || conversation));
  const msIntegration = useNewChatMsIntegration(currentCustomer);
  const formMsContext = isMsWhatsapp ? msIntegration : undefined;
  const useQrForm = isMsWhatsapp
    ? msIntegration.isQrSelected
    : Boolean(retailerInfo.qr_integrated);

  /**
   * Función para avanzar al siguiente step en el flujo.
   * 
   * Permite navegar hacia adelante en el proceso de creación de chat,
   * con soporte para saltar múltiples steps si es necesario.
   */
  const nextStep = useCallback((stepsToSum = 1) => {
    setStep(prevStep => prevStep + stepsToSum);
  }, []);

  /**
   * Función para retroceder al step anterior en el flujo.
   * 
   * Permite navegación hacia atrás en el proceso, utilizada
   * principalmente por botones de "Atrás" en la interfaz.
   */
  const prevStep = useCallback(() => {
    setStep(prevStep => prevStep - 1);
  }, []);

  /**
   * Función para reiniciar el flujo al primer step.
   * 
   * Resetea completamente el proceso de creación de chat,
   * volviendo al estado inicial de búsqueda de clientes.
   */
  const resetSteps = useCallback(() => {
    setStep(1);
  }, []);

  /**
   * Función para manejar la selección de un cliente y avanzar al step de envío.
   * 
   * Ejecuta las acciones Redux necesarias para establecer el cliente seleccionado
   * y avanza automáticamente al step final para envío de mensajes.
   */
  const handleCustomerSelect = useCallback((customer) => {
    dispatch({ type: 'CREATE_CUSTOMER_FROM_CHAT', data: { customer, selected: true } });
    setStep(3);
  }, [dispatch]);

  /**
   * Función para cerrar el sidebar de nuevo chat.
   * 
   * Dispara la acción Redux para ocultar el sidebar y limpiar el estado
   * relacionado con la creación de chats.
   */
  const closeModal = useCallback(() => {
    dispatch({ type: 'CREATE_CUSTOMER_FROM_CHAT', data: { customer: undefined, selected: false } });
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR, payload: { componentKey: 'NewChat' } });
  }, [dispatch]);

  /**
   * Selección dinámica del componente a renderizar según el step actual.
   * 
   * Determina qué componente mostrar basado en el step y la configuración
   * del retailer, manejando la lógica de flujo completa del sidebar.
   */
  // Memoizar selectStep para evitar re-renders innecesarios
  const selectStep = useMemo(() => {
    if (step === 1) {
      return <FindChat onCustomerSelect={handleCustomerSelect} />;
    }
    if (step === 2) {
      return <NewChatForm onSuccess={nextStep} />;
    }
    if (step === 3 && useQrForm) {
      return (
        <QrMessage
          type={type}
          closeModal={closeModal}
          currentCustomer={currentCustomer}
          hasCustomer={hasCustomer}
          onOutboundSendingChange={setOutboundSending}
          msContext={formMsContext}
        />
      );
    }
    if (step === 3 && !useQrForm) {
      return (
        <ApiTemplateMessage
          type={type}
          closeModal={closeModal}
          currentCustomer={currentCustomer}
          hasCustomer={hasCustomer}
          onOutboundSendingChange={setOutboundSending}
          msContext={formMsContext}
        />
      );
    }
    return null;
  }, [
    step,
    handleCustomerSelect,
    nextStep,
    useQrForm,
    formMsContext,
    type,
    closeModal,
    currentCustomer,
    hasCustomer,
    setOutboundSending
  ]);

  /**
   * Función para disparar programaticamente el submit de formularios por ID.
   * 
   * Busca un formulario específico en el DOM y dispara su evento submit,
   * utilizada para coordinar envios desde botones externos al formulario.
   */
  // Memoizar la función triggerFormSubmit para evitar re-creaciones
  const triggerFormSubmit = useCallback((formId) => {
    const form = document.querySelector(`form[id="${formId}"]`);
    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit();
      } catch (e) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }, []);

  /**
   * Función de navegación condicional: retroceder o reiniciar según contexto.
   * 
   * Determina la acción de navegación apropiada basada en si hay un
   * cliente preseleccionado o si el usuario está en flujo normal.
   */
  // Memoizar función backOrReset
  const backOrReset = useCallback(() => {
    if (selected) {
      resetSteps();
    } else {
      prevStep();
    }
  }, [selected, resetSteps, prevStep]);

  /**
   * Configuración dinámica de botones y acciones para cada step del flujo.
   * 
   * Genera la configuración completa de UI para el RightModalLayout basada
   * en el step actual, tipo de integración y contexto del usuario.
   */
  // Configuración de botones y acciones para cada paso
  const stepConfig = useMemo(() => {
    const formId = useQrForm ? "qr-message-form" : "gs-message-form";
    
    switch (step) {
      case 1: // FindChat
        return {
          title: t('title.newChat'),
          cancelLabel: null,
          submitLabel: t('buttons.newContact'),
          cancelAction: null,
          submitAction: () => nextStep(),
          showSubmit: true,
          submitDisabled: false
        };
      case 2: // NewChatForm
        return {
          title: t('title.newContact'),
          cancelLabel: t('buttons.back'),
          submitLabel: t('buttons.continue'),
          cancelAction: () => prevStep(),
          submitAction: () => triggerFormSubmit("new-chat-form"),
          showSubmit: true,
          submitDisabled: false
        };
      case 3: // QrMessage o ApiTemplateMessage
        return {
          title: useQrForm ? t('title.sendMessage') : t('title.sendWithTemplate'),
          cancelLabel: hasCustomer ? null : t('buttons.back'),
          submitLabel: t('buttons.sendMessage'),
          cancelAction: hasCustomer ? null : () => backOrReset(),
          submitAction: () => triggerFormSubmit(formId),
          showSubmit: true,
          submitDisabled: isOutboundSending || (isMsWhatsapp && !msIntegration.selectedIntegration)
        };
      default:
        return {
          title: t('title.newChat'),
          cancelLabel: null,
          submitLabel: null,
          cancelAction: null,
          submitAction: null,
          showSubmit: false,
          submitDisabled: false
        };
    }
  }, [
    step,
    useQrForm,
    isMsWhatsapp,
    msIntegration.selectedIntegration,
    hasCustomer,
    t,
    nextStep,
    prevStep,
    backOrReset,
    triggerFormSubmit,
    isOutboundSending
  ]);

  /**
   * Contenido memoizado del modal según estado de integración y step actual.
   * 
   * Determina qué contenido renderizar dentro del RightModalLayout,
   * incluyendo validaciones de integración y envoltorio de formularios.
   */
  // Memoizar el contenido del modal para optimizar renders
  const modalContent = useMemo(() => {
    if (!isWhatsappConnected) {
      return <EmptyScreenInboxMC platform="whatsapp_modal" />;
    }

    if (step === 1) {
      return (
        <FormProviderMC methods={methods}>
          {selectStep}
        </FormProviderMC>
      );
    }

    return selectStep;
  }, [isWhatsappConnected, step, methods, selectStep]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={stepConfig.title}
        closeAction={closeModal}
        cancelLabel={stepConfig.cancelLabel}
        submitLabel={stepConfig.submitLabel}
        cancelAction={stepConfig.cancelAction}
        submitAction={stepConfig.submitAction}
        submitDisabled={stepConfig.submitDisabled}
      >
        {modalContent}
      </RightModalLayout>
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 * 
 * @type {string}
 */
NewChat.displayName = 'NewChat';

export default NewChat;
